/* Reed Quiz — Crook & Staple
 * Lazy-loaded Vue 3 quiz that recommends reeds from the active collection.
 * No build step: question/answer config, scoring, and Vue component all live here.
 */

// ============================================================================
// Quiz config — questions
// ============================================================================

const COMMON_FREQUENCY = {
  key: 'frequency',
  title: 'How often do you play?',
  options: [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'occasional', label: 'Occasionally' },
  ],
};

const COMMON_YEARS = {
  key: 'years',
  title: 'How long have you been playing?',
  options: [
    { value: 'under1', label: 'Under 1 year' },
    { value: '2-4', label: '2–4 years' },
    { value: '5-10', label: '5–10 years' },
    { value: '10+', label: '10+ years' },
  ],
};

const COMMON_LEVEL = {
  key: 'level',
  title: 'What is your experience level?',
  options: [
    { value: 'beginner', label: 'Beginner' },
    { value: 'amateur', label: 'Amateur' },
    { value: 'professional', label: 'Professional' },
  ],
};

const COMMON_TONE = {
  key: 'tone',
  title: 'Do you prefer a darker or brighter tone when playing?',
  options: [
    { value: 'darker', label: 'Darker' },
    { value: 'brighter', label: 'Brighter' },
    { value: 'balanced', label: 'No preference' },
  ],
};

const COMMON_CONCERNS = {
  key: 'concerns',
  title: 'Do you have any specific concerns about your current reed?',
  description: 'Select any that apply — or pick None.',
  multiSelect: true,
  options: [
    { value: 'control', label: 'Difficulty in control' },
    { value: 'sound', label: 'Poor sound quality' },
    { value: 'durability', label: 'Lack of durability' },
    { value: 'sharp', label: 'Being too sharp' },
    { value: 'quiet', label: 'Hard to play quiet' },
    { value: 'none', label: 'None' },
  ],
};

const BASSOON_MAKE = {
  key: 'make',
  title: 'What make of bassoon do you play on?',
  options: [
    { value: 'unsure', label: 'Not sure', description: 'No problem — we\'ll still suggest reeds.' },
    { value: 'adler', label: 'Adler' },
    { value: 'amati', label: 'Amati' },
    { value: 'bell', label: 'Bell' },
    { value: 'conn-selmer', label: 'Conn-Selmer' },
    { value: 'fox', label: 'Fox' },
    { value: 'heckel', label: 'Heckel' },
    { value: 'kohlert', label: 'Kohlert' },
    { value: 'leitzinger', label: 'Leitzinger' },
    { value: 'linton', label: 'Linton' },
    { value: 'moennig', label: 'Moennig' },
    { value: 'mollenhauer', label: 'Mollenhauer' },
    { value: 'moosmann', label: 'Moosmann' },
    { value: 'puchner', label: 'Püchner' },
    { value: 'schreiber', label: 'Schreiber' },
    { value: 'walter', label: 'Walter' },
    { value: 'wolf', label: 'Wolf' },
    { value: 'yamaha', label: 'Yamaha' },
    { value: 'other', label: 'Other' },
  ],
};

const OBOE_MAKE = {
  key: 'make',
  title: 'What make of oboe do you play on?',
  options: [
    { value: 'unsure', label: 'Not sure', description: 'No problem — we\'ll still suggest reeds.' },
    { value: 'buffet', label: 'Buffet' },
    { value: 'cabart', label: 'Cabart' },
    { value: 'fossati', label: 'Fossati' },
    { value: 'howarth', label: 'Howarth' },
    { value: 'loree', label: 'Lorée' },
    { value: 'marigaux', label: 'Marigaux' },
    { value: 'monnig', label: 'Mönnig' },
    { value: 'puchner', label: 'Püchner' },
    { value: 'rigoutat', label: 'Rigoutat' },
    { value: 'strasser', label: 'Strasser' },
    { value: 'yamaha', label: 'Yamaha' },
    { value: 'other', label: 'Other' },
  ],
};

const BASSOON_QUESTIONS = [
  COMMON_FREQUENCY,
  COMMON_YEARS,
  COMMON_LEVEL,
  BASSOON_MAKE,
  COMMON_TONE,
  COMMON_CONCERNS,
];

const OBOE_QUESTIONS = [
  COMMON_FREQUENCY,
  COMMON_YEARS,
  COMMON_LEVEL,
  OBOE_MAKE,
  COMMON_TONE,
  COMMON_CONCERNS,
];

// ============================================================================
// Quiz config — product weight profiles
// Each key is a Shopify product handle. Each profile stores how strongly the
// product matches each possible answer, summed at scoring time.
// ============================================================================

const BASSOON_PROFILES = {
  'kge-student-bassoon-reed': {
    frequency: { daily: 1, weekly: 2, occasional: 3 },
    years: { under1: 3, '2-4': 1, '5-10': 0, '10+': 0 },
    level: { beginner: 3, amateur: 1, professional: 0 },
    tone: { darker: 1, brighter: 1, balanced: 2 },
    concerns: { control: 3, sound: 1, durability: 1, sharp: 2, quiet: 1 },
  },
  'kge-bassoon-reed': {
    frequency: { daily: 2, weekly: 3, occasional: 2 },
    years: { under1: 1, '2-4': 3, '5-10': 2, '10+': 1 },
    level: { beginner: 1, amateur: 3, professional: 1 },
    tone: { darker: 1, brighter: 2, balanced: 3 },
    concerns: { control: 2, sound: 2, durability: 2, sharp: 1, quiet: 1 },
  },
  'marsden-bassoon-reeds-cardiff': {
    frequency: { daily: 3, weekly: 2, occasional: 1 },
    years: { under1: 0, '2-4': 1, '5-10': 3, '10+': 3 },
    level: { beginner: 0, amateur: 2, professional: 3 },
    tone: { darker: 1, brighter: 2, balanced: 3 },
    concerns: { control: 2, sound: 3, durability: 2, sharp: 1, quiet: 2 },
    make: { fox: 1, yamaha: 1, 'conn-selmer': 1 },
  },
  'urbach-bassoon-reed': {
    frequency: { daily: 3, weekly: 2, occasional: 1 },
    years: { under1: 0, '2-4': 1, '5-10': 3, '10+': 3 },
    level: { beginner: 0, amateur: 2, professional: 3 },
    tone: { darker: 3, brighter: 1, balanced: 2 },
    concerns: { control: 2, sound: 3, durability: 2, sharp: 0, quiet: 2 },
    make: { heckel: 1, puchner: 1, moosmann: 1, schreiber: 1, mollenhauer: 1, adler: 1, kohlert: 1, wolf: 1 },
  },
  'windeler-bassoon-reed-bd2': {
    frequency: { daily: 3, weekly: 3, occasional: 1 },
    years: { under1: 0, '2-4': 1, '5-10': 3, '10+': 3 },
    level: { beginner: 0, amateur: 2, professional: 3 },
    tone: { darker: 3, brighter: 1, balanced: 2 },
    concerns: { control: 2, sound: 3, durability: 2, sharp: 0, quiet: 1 },
    make: { heckel: 1, puchner: 1, moosmann: 1, schreiber: 1, mollenhauer: 1, adler: 1, kohlert: 1, wolf: 1 },
  },
  'windeler-medium-bassoon-reed-bd3': {
    frequency: { daily: 2, weekly: 3, occasional: 1 },
    years: { under1: 0, '2-4': 0, '5-10': 2, '10+': 3 },
    level: { beginner: 0, amateur: 1, professional: 3 },
    tone: { darker: 3, brighter: 0, balanced: 2 },
    concerns: { control: 2, sound: 2, durability: 1, sharp: 0, quiet: 3 },
    make: { heckel: 1, puchner: 1, moosmann: 1, schreiber: 1, mollenhauer: 1, adler: 1, kohlert: 1, wolf: 1 },
  },
  'alaw-bassoon-reed': {
    frequency: { daily: 3, weekly: 3, occasional: 2 },
    years: { under1: 0, '2-4': 2, '5-10': 3, '10+': 3 },
    level: { beginner: 0, amateur: 3, professional: 3 },
    tone: { darker: 2, brighter: 2, balanced: 3 },
    concerns: { control: 2, sound: 3, durability: 2, sharp: 1, quiet: 2 },
  },
  'argento-bassoon-reed': {
    frequency: { daily: 3, weekly: 3, occasional: 1 },
    years: { under1: 0, '2-4': 1, '5-10': 2, '10+': 3 },
    level: { beginner: 0, amateur: 2, professional: 3 },
    tone: { darker: 3, brighter: 1, balanced: 2 },
    concerns: { control: 3, sound: 2, durability: 1, sharp: 0, quiet: 3 },
  },
};

const OBOE_PROFILES = {
  'student-oboe-reed-by-crook-staple-medium-soft': {
    frequency: { daily: 1, weekly: 2, occasional: 3 },
    years: { under1: 3, '2-4': 2, '5-10': 0, '10+': 0 },
    level: { beginner: 3, amateur: 1, professional: 0 },
    tone: { darker: 1, brighter: 1, balanced: 2 },
    concerns: { control: 3, sound: 1, durability: 1, sharp: 2, quiet: 1 },
  },
  'intermediate-oboe-reed-by-c-s': {
    frequency: { daily: 2, weekly: 3, occasional: 2 },
    years: { under1: 1, '2-4': 3, '5-10': 1, '10+': 0 },
    level: { beginner: 2, amateur: 3, professional: 0 },
    tone: { darker: 1, brighter: 1, balanced: 2 },
    concerns: { control: 3, sound: 2, durability: 1, sharp: 1, quiet: 1 },
  },
  'winfield-standard-oboe-reed': {
    frequency: { daily: 2, weekly: 3, occasional: 2 },
    years: { under1: 1, '2-4': 3, '5-10': 2, '10+': 1 },
    level: { beginner: 1, amateur: 3, professional: 1 },
    tone: { darker: 1, brighter: 1, balanced: 3 },
    concerns: { control: 2, sound: 2, durability: 2, sharp: 1, quiet: 1 },
  },
  'winfield-continental-oboe-reed-medium': {
    frequency: { daily: 2, weekly: 3, occasional: 1 },
    years: { under1: 0, '2-4': 2, '5-10': 3, '10+': 2 },
    level: { beginner: 0, amateur: 3, professional: 2 },
    tone: { darker: 3, brighter: 0, balanced: 2 },
    concerns: { control: 2, sound: 3, durability: 2, sharp: 0, quiet: 2 },
    make: { puchner: 1, monnig: 1, strasser: 1 },
  },
  'academy-oboe-reed-by-crook-staple': {
    frequency: { daily: 3, weekly: 3, occasional: 1 },
    years: { under1: 0, '2-4': 2, '5-10': 3, '10+': 1 },
    level: { beginner: 0, amateur: 3, professional: 1 },
    tone: { darker: 1, brighter: 1, balanced: 3 },
    concerns: { control: 2, sound: 3, durability: 2, sharp: 1, quiet: 2 },
  },
  'dorado-oboe-reed-by-crook-staple': {
    frequency: { daily: 2, weekly: 3, occasional: 1 },
    years: { under1: 0, '2-4': 2, '5-10': 3, '10+': 2 },
    level: { beginner: 0, amateur: 3, professional: 2 },
    tone: { darker: 3, brighter: 0, balanced: 1 },
    concerns: { control: 1, sound: 3, durability: 1, sharp: 0, quiet: 2 },
  },
  'kge-standard-oboe-reed-medium': {
    frequency: { daily: 2, weekly: 3, occasional: 2 },
    years: { under1: 0, '2-4': 2, '5-10': 2, '10+': 1 },
    level: { beginner: 0, amateur: 3, professional: 1 },
    tone: { darker: 1, brighter: 1, balanced: 3 },
    concerns: { control: 3, sound: 2, durability: 2, sharp: 1, quiet: 1 },
  },
  'professional-winfield-oboe-reed-brass-staple-medium': {
    frequency: { daily: 3, weekly: 2, occasional: 0 },
    years: { under1: 0, '2-4': 1, '5-10': 2, '10+': 3 },
    level: { beginner: 0, amateur: 1, professional: 3 },
    tone: { darker: 0, brighter: 3, balanced: 1 },
    concerns: { control: 2, sound: 3, durability: 2, sharp: 1, quiet: 1 },
    make: { buffet: 1, marigaux: 1, loree: 1, fossati: 1, rigoutat: 1, cabart: 1, howarth: 1 },
  },
  'professional-oboe-reed-by-crook-staple': {
    frequency: { daily: 3, weekly: 2, occasional: 0 },
    years: { under1: 0, '2-4': 1, '5-10': 3, '10+': 3 },
    level: { beginner: 0, amateur: 2, professional: 3 },
    tone: { darker: 1, brighter: 1, balanced: 3 },
    concerns: { control: 2, sound: 3, durability: 2, sharp: 1, quiet: 2 },
    make: { howarth: 1, loree: 1, marigaux: 1 },
  },
  'kge-professional-us-oboe-reed': {
    frequency: { daily: 3, weekly: 2, occasional: 0 },
    years: { under1: 0, '2-4': 1, '5-10': 2, '10+': 3 },
    level: { beginner: 0, amateur: 1, professional: 3 },
    tone: { darker: 0, brighter: 3, balanced: 1 },
    concerns: { control: 1, sound: 3, durability: 1, sharp: 1, quiet: 1 },
    make: { buffet: 1, marigaux: 1, loree: 1, fossati: 1, rigoutat: 1, cabart: 1 },
  },
  'winfield-professional-oboe-reed-gold-staple-medium': {
    frequency: { daily: 3, weekly: 2, occasional: 0 },
    years: { under1: 0, '2-4': 0, '5-10': 2, '10+': 3 },
    level: { beginner: 0, amateur: 1, professional: 3 },
    tone: { darker: 3, brighter: 0, balanced: 1 },
    concerns: { control: 2, sound: 3, durability: 2, sharp: 0, quiet: 2 },
  },
  'kge-professional-german-oboe-reed': {
    frequency: { daily: 3, weekly: 1, occasional: 0 },
    years: { under1: 0, '2-4': 0, '5-10': 2, '10+': 3 },
    level: { beginner: 0, amateur: 0, professional: 3 },
    tone: { darker: 3, brighter: 0, balanced: 1 },
    concerns: { control: 1, sound: 3, durability: 2, sharp: 0, quiet: 1 },
    make: { puchner: 1, monnig: 1, strasser: 1 },
  },
};

const BASSOON_FALLBACK = ['alaw-bassoon-reed', 'marsden-bassoon-reeds-cardiff', 'kge-bassoon-reed'];
const OBOE_FALLBACK = ['professional-oboe-reed-by-crook-staple', 'academy-oboe-reed-by-crook-staple', 'student-oboe-reed-by-crook-staple-medium-soft'];

const OWN_BRAND_VENDORS = ['crook & staple', 'crook and staple', 'winfield'];

// Friendly per-dimension explanations rendered on result cards as small chips.
// Top 2 highest-scoring dimensions for each product become the visible reasons.
const REASON_LABELS = {
  level: {
    beginner: 'Great for beginners',
    amateur: 'Suits amateur players',
    professional: 'Built for professionals',
  },
  frequency: {
    daily: 'Holds up to daily play',
    weekly: 'Reliable for weekly practice',
    occasional: 'Easy pick-up-and-play',
  },
  years: {
    'under1': 'Forgiving for new players',
    '2-4': 'Right for early years',
    '5-10': 'Made for experienced players',
    '10+': 'Tailored for seasoned players',
  },
  tone: {
    darker: 'Darker, warmer tone',
    brighter: 'Brighter, focused tone',
    balanced: 'Balanced tone',
  },
  concerns: {
    control: 'Easier to control',
    sound: 'Excellent sound quality',
    durability: 'Built to last',
    sharp: 'Won’t sit sharp',
    quiet: 'Plays quietly with ease',
  },
  make: {
    heckel: 'Pairs well with German bassoons',
    puchner: 'Pairs well with German bassoons',
    moosmann: 'Pairs well with German bassoons',
    schreiber: 'Pairs well with German bassoons',
    mollenhauer: 'Pairs well with German bassoons',
    adler: 'Pairs well with German bassoons',
    kohlert: 'Pairs well with German bassoons',
    wolf: 'Pairs well with German bassoons',
    fox: 'Pairs well with North American bassoons',
    yamaha: 'Pairs well with Yamaha instruments',
    'conn-selmer': 'Pairs well with North American bassoons',
    buffet: 'Suits French-school oboes',
    marigaux: 'Suits French-school oboes',
    loree: 'Suits French-school oboes',
    fossati: 'Suits French-school oboes',
    rigoutat: 'Suits French-school oboes',
    cabart: 'Suits French-school oboes',
    howarth: 'Suits British-school oboes',
    monnig: 'Suits German-school oboes',
    strasser: 'Suits German-school oboes',
  },
};

const CONFIG = {
  bassoon: { questions: BASSOON_QUESTIONS, profiles: BASSOON_PROFILES, fallback: BASSOON_FALLBACK, collectionPath: '/collections/bassoon-reeds' },
  oboe:    { questions: OBOE_QUESTIONS,    profiles: OBOE_PROFILES,    fallback: OBOE_FALLBACK,    collectionPath: '/collections/oboe-reeds' },
};

// ============================================================================
// Recommendation engine
// ============================================================================

function isOwnBrand(vendor) {
  if (!vendor) return false;
  return OWN_BRAND_VENDORS.includes(String(vendor).toLowerCase().trim());
}

function scoreProduct(profile, answers, product) {
  let total = 0;
  const matches = []; // [{ dim, value, score }] — used to surface "why this matches"
  for (const dim of Object.keys(answers)) {
    const value = answers[dim];
    const dimWeights = profile[dim];
    if (!dimWeights) continue;
    if (Array.isArray(value)) {
      for (const v of value) {
        const s = dimWeights[v];
        if (typeof s === 'number' && s > 0) {
          matches.push({ dim, value: v, score: s });
          total += s;
        }
      }
    } else {
      const s = dimWeights[value];
      if (typeof s === 'number' && s > 0) {
        matches.push({ dim, value, score: s });
        total += s;
      }
    }
  }
  if (isOwnBrand(product.vendor) && answers.level && answers.level !== 'beginner') total += 0.5;
  if (product.available === false) total -= 0.75;
  return { total, matches };
}

function reasonsFromMatches(matches, max = 2) {
  const ordered = matches.slice().sort((a, b) => b.score - a.score);
  const seenDims = new Set();
  const reasons = [];
  for (const m of ordered) {
    if (seenDims.has(m.dim)) continue;
    const label = REASON_LABELS[m.dim] && REASON_LABELS[m.dim][m.value];
    if (!label) continue;
    seenDims.add(m.dim);
    reasons.push(label);
    if (reasons.length >= max) break;
  }
  return reasons;
}

function pickRecommendations(answers, products, profiles, fallbackHandles) {
  const productsByHandle = new Map(products.map(p => [p.handle, p]));
  const scored = products
    .filter(p => profiles[p.handle])
    .map(p => {
      const { total, matches } = scoreProduct(profiles[p.handle], answers, p);
      return { product: p, score: total, matches };
    })
    .sort((a, b) => b.score - a.score);

  const top = scored.filter(s => s.score > 0).slice(0, 3);
  const used = new Set(top.map(s => s.product.handle));

  if (top.length < 3) {
    for (const handle of fallbackHandles) {
      if (top.length >= 3) break;
      if (used.has(handle)) continue;
      const p = productsByHandle.get(handle);
      if (p) { top.push({ product: p, score: 0, matches: [] }); used.add(handle); }
    }
  }

  return top.map(s => Object.assign({}, s.product, { _reasons: reasonsFromMatches(s.matches) }));
}

// ============================================================================
// Vue component factory (built lazily once Vue is loaded)
// ============================================================================

let _vuePromise = null;
function loadVue() {
  if (!_vuePromise) {
    _vuePromise = import(window.__reedQuizVueUrl);
  }
  return _vuePromise;
}

function buildQuizComponent(Vue, options) {
  const { reactive, computed, onMounted, onBeforeUnmount, nextTick, h, Transition } = Vue;
  const { instrument, products, config, autoStart, onClose, welcomeTitle, welcomeBody } = options;

  const STEP_WELCOME = 0;
  const STEP_QUESTION = 1;
  const STEP_TRANSITION = 2;
  const STEP_RESULTS = 3;

  return {
    setup() {
      const storageKey = `reed-quiz-state-${instrument}`;
      let restored = null;
      try { restored = JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch (_) {}

      const state = reactive({
        step: restored?.step ?? (autoStart ? STEP_QUESTION : STEP_WELCOME),
        questionIndex: restored?.questionIndex ?? 0,
        answers: restored?.answers ?? {},
        cartLoading: null,
        addedHandles: [],
        cartError: null,
      });

      const totalQuestions = config.questions.length;

      const currentQuestion = computed(() =>
        state.step === STEP_QUESTION ? config.questions[state.questionIndex] : null
      );

      const progress = computed(() => {
        if (state.step === STEP_WELCOME) return 0;
        if (state.step === STEP_QUESTION) return Math.round(((state.questionIndex + 1) / totalQuestions) * 100);
        if (state.step === STEP_TRANSITION) return 100;
        return 100;
      });

      const isAnswered = computed(() => {
        const q = currentQuestion.value;
        if (!q) return true;
        const a = state.answers[q.key];
        if (q.multiSelect) return Array.isArray(a) && a.length > 0;
        return a != null && a !== '';
      });

      const recommendations = computed(() =>
        state.step === STEP_RESULTS
          ? pickRecommendations(state.answers, products, config.profiles, config.fallback)
          : []
      );

      function persist() {
        try {
          sessionStorage.setItem(storageKey, JSON.stringify({
            step: state.step,
            questionIndex: state.questionIndex,
            answers: state.answers,
          }));
        } catch (_) {}
      }

      function start() {
        state.step = STEP_QUESTION;
        state.questionIndex = 0;
        persist();
      }

      function isOptionSelected(q, val) {
        const a = state.answers[q.key];
        if (q.multiSelect) return Array.isArray(a) && a.includes(val);
        return a === val;
      }

      function pickAnswer(q, value) {
        if (q.multiSelect) {
          const cur = Array.isArray(state.answers[q.key]) ? state.answers[q.key].slice() : [];
          const idx = cur.indexOf(value);
          if (value === 'none') {
            state.answers[q.key] = idx >= 0 ? [] : ['none'];
          } else {
            const next = idx >= 0 ? cur.filter(v => v !== value) : cur.concat(value);
            state.answers[q.key] = next.filter(v => v !== 'none');
          }
        } else {
          state.answers[q.key] = value;
          // Single-select advances on a short delay so the press feels confirmed
          setTimeout(() => {
            if (currentQuestion.value && currentQuestion.value.key === q.key && state.answers[q.key] === value) {
              advance();
            }
          }, 220);
        }
        persist();
      }

      function advance() {
        if (!isAnswered.value) return;
        if (state.questionIndex < totalQuestions - 1) {
          state.questionIndex++;
        } else {
          state.step = STEP_TRANSITION;
          setTimeout(() => { state.step = STEP_RESULTS; persist(); }, 750);
        }
        persist();
      }

      function back() {
        if (state.step === STEP_RESULTS) {
          state.step = STEP_QUESTION;
          state.questionIndex = totalQuestions - 1;
        } else if (state.step === STEP_QUESTION && state.questionIndex > 0) {
          state.questionIndex--;
        } else if (state.step === STEP_QUESTION) {
          state.step = STEP_WELCOME;
        }
        persist();
      }

      function restart() {
        try { sessionStorage.removeItem(storageKey); } catch (_) {}
        state.answers = {};
        state.questionIndex = 0;
        state.step = STEP_WELCOME;
        state.addedHandles = [];
        state.cartError = null;
      }

      async function addToCart(product) {
        if (!product.default_variant_id || state.cartLoading) return;
        state.cartLoading = product.handle;
        state.cartError = null;
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: product.default_variant_id, quantity: 1 }),
          });
          if (!res.ok) throw new Error('add-failed');
          if (!state.addedHandles.includes(product.handle)) {
            state.addedHandles = state.addedHandles.concat(product.handle);
          }
          // Let the theme update its own cart UI (count, drawer) without leaving the page
          document.dispatchEvent(new CustomEvent('reed-quiz:cart-added', { detail: { product }, bubbles: true }));
          // Best-effort silent refresh of any common Empire cart endpoints
          if (window.Theme && window.Theme.cart && typeof window.Theme.cart.refresh === 'function') {
            try { window.Theme.cart.refresh(); } catch (_) {}
          } else {
            // Trigger the change event some themes listen to via the cart-ajax section
            document.dispatchEvent(new CustomEvent('cart:updated'));
          }
        } catch (_) {
          state.cartError = product.handle;
          setTimeout(() => { if (state.cartError === product.handle) state.cartError = null; }, 4000);
        } finally {
          state.cartLoading = null;
        }
      }

      function onKeydown(e) {
        if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) return;
        if (e.key === 'Escape') {
          if (typeof onClose === 'function') onClose();
          return;
        }
        if (state.step === STEP_QUESTION) {
          const q = currentQuestion.value;
          if (!q) return;
          const num = parseInt(e.key, 10);
          if (!Number.isNaN(num) && num >= 1 && num <= q.options.length) {
            pickAnswer(q, q.options[num - 1].value);
            e.preventDefault();
            return;
          }
          if (e.key === 'Enter' && isAnswered.value) {
            advance();
            e.preventDefault();
          } else if (e.key === 'Backspace' || e.key === 'ArrowLeft') {
            back();
            e.preventDefault();
          }
        }
      }

      onMounted(() => { document.addEventListener('keydown', onKeydown); });
      onBeforeUnmount(() => { document.removeEventListener('keydown', onKeydown); });

      function focusFirstOption(el) {
        nextTick(() => {
          const target = el && el.querySelector
            ? el.querySelector('.reed-quiz-option:not([disabled])')
            : null;
          if (target) target.focus({ preventScroll: true });
        });
      }

      return {
        state, totalQuestions, currentQuestion, progress, isAnswered, recommendations,
        start, pickAnswer, advance, back, restart, addToCart, isOptionSelected,
        focusFirstOption,
        STEP_WELCOME, STEP_QUESTION, STEP_TRANSITION, STEP_RESULTS,
        instrument, config, products, welcomeTitle, welcomeBody,
      };
    },

    template: /* html */ `
      <div class="reed-quiz-app" :data-step="state.step">
        <div class="reed-quiz-progress" v-show="state.step === STEP_QUESTION || state.step === STEP_TRANSITION" aria-hidden="true">
          <div class="reed-quiz-progress__bar" :style="{ width: progress + '%' }"></div>
        </div>

        <button v-if="state.step === STEP_QUESTION" type="button" class="reed-quiz-back" @click="back" aria-label="Go to previous question">
          <span aria-hidden="true">←</span> Go back
        </button>

        <Transition name="reed-quiz-fade" mode="out-in">
          <div v-if="state.step === STEP_WELCOME" key="welcome" class="reed-quiz-step reed-quiz-step--welcome">
            <h2 class="reed-quiz-welcome__title">{{ welcomeTitle }}</h2>
            <p class="reed-quiz-welcome__body">{{ welcomeBody }}</p>
            <button type="button" class="reed-quiz-btn reed-quiz-btn--primary reed-quiz-btn--lg" @click="start">Start</button>
          </div>

          <div
            v-else-if="state.step === STEP_QUESTION"
            :key="'q-' + state.questionIndex"
            class="reed-quiz-step reed-quiz-step--question"
            :ref="el => el && focusFirstOption(el)"
          >
            <h2 class="reed-quiz-question__title">{{ currentQuestion.title }}</h2>
            <p v-if="currentQuestion.description" class="reed-quiz-question__description">{{ currentQuestion.description }}</p>
            <div
              class="reed-quiz-options"
              :class="{ 'reed-quiz-options--grid': currentQuestion.options.length > 6, 'reed-quiz-options--multi': currentQuestion.multiSelect }"
              role="group"
            >
              <button
                v-for="(opt, i) in currentQuestion.options"
                :key="opt.value"
                type="button"
                class="reed-quiz-option"
                :class="{ 'is-selected': isOptionSelected(currentQuestion, opt.value) }"
                @click="pickAnswer(currentQuestion, opt.value)"
                :aria-pressed="isOptionSelected(currentQuestion, opt.value)"
              >
                <span class="reed-quiz-option__indicator" aria-hidden="true"></span>
                <span class="reed-quiz-option__text">
                  <span class="reed-quiz-option__label">{{ opt.label }}</span>
                  <span v-if="opt.description" class="reed-quiz-option__description">{{ opt.description }}</span>
                </span>
              </button>
            </div>
            <button
              v-if="currentQuestion.multiSelect || state.questionIndex === totalQuestions - 1 || isAnswered"
              type="button"
              class="reed-quiz-btn reed-quiz-btn--primary reed-quiz-btn--next"
              :disabled="!isAnswered"
              @click="advance"
            >
              {{ state.questionIndex === totalQuestions - 1 ? 'See my recommendations' : 'Next question' }}
            </button>
          </div>

          <div v-else-if="state.step === STEP_TRANSITION" key="transition" class="reed-quiz-step reed-quiz-step--transition" role="status" aria-live="polite">
            <div class="reed-quiz-transition__spinner" aria-hidden="true"></div>
            <h2 class="reed-quiz-transition__title">Finding your ideal reeds…</h2>
          </div>

          <div v-else-if="state.step === STEP_RESULTS" key="results" class="reed-quiz-step reed-quiz-step--results">
            <header class="reed-quiz-results__header">
              <span class="reed-quiz-results__eyebrow">Your match</span>
              <h2 class="reed-quiz-results__title">Our top picks for you</h2>
              <p class="reed-quiz-results__subtitle">Hand-picked from our collection based on your answers.</p>
            </header>

            <div
              v-if="state.addedHandles.length > 0"
              class="reed-quiz-toast"
              role="status"
              aria-live="polite"
            >
              <span class="reed-quiz-toast__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </span>
              <span class="reed-quiz-toast__text">
                {{ state.addedHandles.length }} reed{{ state.addedHandles.length === 1 ? '' : 's' }} added to your cart
              </span>
              <a class="reed-quiz-toast__link" href="/cart">View cart →</a>
            </div>

            <div class="reed-quiz-results__cards">
              <article
                v-for="(p, i) in recommendations"
                :key="p.handle"
                class="reed-quiz-card"
                :class="{ 'reed-quiz-card--hero': i === 0, 'reed-quiz-card--added': state.addedHandles.includes(p.handle) }"
                :style="{ '--rq-card-delay': (i * 80) + 'ms' }"
              >
                <span v-if="i === 0" class="reed-quiz-card__ribbon">Top pick</span>
                <a class="reed-quiz-card__media" :href="p.url" :aria-label="p.title">
                  <img v-if="p.image_url" :src="p.image_url" :alt="p.image_alt" :loading="i === 0 ? 'eager' : 'lazy'" width="600" height="600">
                  <div v-else class="reed-quiz-card__media-placeholder" aria-hidden="true"></div>
                </a>
                <div class="reed-quiz-card__body">
                  <div v-if="Number(p.rating) > 0 && p.rating_count > 0" class="reed-quiz-card__reviews">
                    <span class="reed-quiz-card__stars" :aria-label="'Rated ' + p.rating + ' out of 5'">
                      <span v-for="n in 5" :key="n" class="reed-quiz-card__star" :class="{ 'is-filled': n <= Math.round(Number(p.rating)) }">★</span>
                    </span>
                    <span class="reed-quiz-card__review-count">{{ p.rating_count }} review{{ p.rating_count === 1 ? '' : 's' }}</span>
                  </div>
                  <h3 class="reed-quiz-card__title"><a :href="p.url">{{ p.title }}</a></h3>
                  <ul v-if="p._reasons && p._reasons.length" class="reed-quiz-card__reasons" aria-label="Why this matches">
                    <li v-for="reason in p._reasons" :key="reason" class="reed-quiz-card__reason">
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                      <span>{{ reason }}</span>
                    </li>
                  </ul>
                  <div class="reed-quiz-card__meta">
                    <span class="reed-quiz-card__price">{{ p.price }}</span>
                    <span v-if="!p.available" class="reed-quiz-card__stock">Backorder</span>
                  </div>
                  <div class="reed-quiz-card__actions">
                    <a class="reed-quiz-btn reed-quiz-btn--secondary" :href="p.url">View product</a>
                    <button
                      type="button"
                      class="reed-quiz-btn reed-quiz-btn--cart"
                      :class="{ 'is-added': state.addedHandles.includes(p.handle), 'is-error': state.cartError === p.handle }"
                      :disabled="state.cartLoading === p.handle"
                      @click="addToCart(p)"
                    >
                      <span v-if="state.cartLoading === p.handle" class="reed-quiz-btn__label">
                        <span class="reed-quiz-btn__spinner" aria-hidden="true"></span>
                        Adding…
                      </span>
                      <span v-else-if="state.cartError === p.handle" class="reed-quiz-btn__label">Try again</span>
                      <span v-else-if="state.addedHandles.includes(p.handle)" class="reed-quiz-btn__label">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                        Added — add another
                      </span>
                      <span v-else class="reed-quiz-btn__label">Add to cart</span>
                    </button>
                  </div>
                </div>
              </article>
            </div>
            <div class="reed-quiz-results__footer">
              <button type="button" class="reed-quiz-btn reed-quiz-btn--text" @click="restart">Take quiz again</button>
              <a class="reed-quiz-btn reed-quiz-btn--text" :href="config.collectionPath">Browse all reeds</a>
            </div>
          </div>
        </Transition>
      </div>
    `,
  };
}

// ============================================================================
// DOM bootstrap
// ============================================================================

function init() {
  const roots = document.querySelectorAll('[data-reed-quiz]');
  roots.forEach(initRoot);
}

function initRoot(root) {
  if (root.__reedQuizInitialized) return;
  root.__reedQuizInitialized = true;

  const banner = root.querySelector('[data-reed-quiz-banner]');
  const expanded = root.querySelector('[data-reed-quiz-expanded]');
  const closeBtn = root.querySelector('[data-reed-quiz-close]');
  const prestart = root.querySelector('[data-reed-quiz-prestart]');
  const productsJsonEl = root.querySelector('[data-reed-quiz-products]');
  const stage = root.querySelector('[data-reed-quiz-stage]');
  const staticWelcomeTitle = root.querySelector('.reed-quiz-welcome__title');
  const staticWelcomeBody = root.querySelector('.reed-quiz-welcome__body');
  const instrument = root.dataset.instrument;
  const config = CONFIG[instrument];

  let mounted = false;
  let pendingAutoStart = false;

  function open() {
    if (root.classList.contains('is-open')) return;
    root.classList.add('is-open');
    expanded.hidden = false;
    expanded.removeAttribute('aria-hidden');
    if (banner) banner.setAttribute('aria-expanded', 'true');
    mountVue();
  }

  function close() {
    if (!root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    if (banner) {
      banner.setAttribute('aria-expanded', 'false');
      banner.focus({ preventScroll: true });
    }
    // Allow CSS exit transition before hiding from a11y tree
    setTimeout(() => {
      if (!root.classList.contains('is-open')) {
        expanded.hidden = true;
        expanded.setAttribute('aria-hidden', 'true');
      }
    }, 320);
  }

  function mountVue() {
    if (mounted || !config) return;
    mounted = true;

    let products = [];
    try { products = JSON.parse((productsJsonEl?.textContent || '[]').trim()); } catch (_) { products = []; }

    const welcomeTitle = staticWelcomeTitle?.textContent?.trim() || 'Find your perfect reed';
    const welcomeBody = staticWelcomeBody?.textContent?.trim() || '';

    loadVue().then((Vue) => {
      const App = buildQuizComponent(Vue, {
        instrument,
        products,
        config,
        autoStart: pendingAutoStart,
        welcomeTitle,
        welcomeBody,
        onClose: close,
      });
      const mountTarget = document.createElement('div');
      mountTarget.className = 'reed-quiz-mount';
      stage.replaceChildren(mountTarget);
      Vue.createApp(App).mount(mountTarget);
    }).catch(() => {
      // If Vue fails to load, leave the static welcome in place — it still has a usable Start button
      // but the Start button won't be wired. Surface a graceful message:
      mounted = false;
      const fallback = document.createElement('p');
      fallback.className = 'reed-quiz-fallback';
      fallback.textContent = 'Sorry, the quiz failed to load. Please refresh the page or browse the collection below.';
      stage.appendChild(fallback);
    });
  }

  // PLP banner click: the banner copy already serves as the welcome, so skip
  // straight to the first question. `pendingAutoStart` is ignored if a saved
  // session restores the user mid-quiz or to results.
  if (banner) {
    banner.addEventListener('click', () => {
      pendingAutoStart = true;
      open();
    });
  }
  if (closeBtn) closeBtn.addEventListener('click', close);

  if (prestart) {
    prestart.addEventListener('click', () => {
      pendingAutoStart = true;
      open();
    });
  }

  // start_open=true (dedicated quiz pages) keeps the welcome screen
  if (root.dataset.startOpen === 'true') {
    open();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
